// src/utils/ImageUtils.js
export const MAX_DELIVERY_LOGO_SIZE = 2 * 1024 * 1024;
export const DELIVERY_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const CATALOG_IMAGE_MAX_SIZE = 60 * 1024;
export const CATALOG_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const CATALOG_IMAGE_ACCEPT = CATALOG_IMAGE_TYPES.join(',');

const CATALOG_IMAGE_EXTENSIONS = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
};
const CATALOG_IMAGE_SCALE_STEPS = [1, 0.85, 0.7, 0.55, 0.4];
const CATALOG_IMAGE_QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45];
const INVALID_CATALOG_IMAGE_MESSAGE = 'Selecione uma imagem válida nos formatos JPEG, PNG ou WebP.';

export const getImageMimeType = (base64, fallbackMimeType = 'image/png') => {
    const normalizedBase64 = String(base64 || '').trim();
    if (normalizedBase64.startsWith('/9j/')) return 'image/jpeg';
    if (normalizedBase64.startsWith('iVBOR')) return 'image/png';
    if (normalizedBase64.startsWith('UklGR')) return 'image/webp';
    return fallbackMimeType;
};

export const createBase64ImageSource = (base64, mimeType) => {
    const normalizedBase64 = String(base64 || '').trim();
    if (!normalizedBase64) return '';
    return `data:${mimeType || getImageMimeType(normalizedBase64)};base64,${normalizedBase64}`;
};

export const validateDeliveryLogoFile = (file) => {
    if (!DELIVERY_LOGO_TYPES.includes(file?.type)) {
        return 'Utilize uma imagem PNG, JPEG ou WebP.';
    }

    if (file.size > MAX_DELIVERY_LOGO_SIZE) {
        return 'A logo do delivery deve possuir no máximo 2 MB.';
    }

    return '';
};

const readFileSignature = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(new Error(INVALID_CATALOG_IMAGE_MESSAGE));
    reader.readAsArrayBuffer(file.slice(0, 12));
});

const getMimeTypeFromSignature = (bytes) => {
    if (bytes.length >= 3 && bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
        return 'image/jpeg';
    }

    if (
        bytes.length >= 8
        && bytes[0] === 0x89
        && bytes[1] === 0x50
        && bytes[2] === 0x4E
        && bytes[3] === 0x47
        && bytes[4] === 0x0D
        && bytes[5] === 0x0A
        && bytes[6] === 0x1A
        && bytes[7] === 0x0A
    ) {
        return 'image/png';
    }

    if (
        bytes.length >= 12
        && bytes[0] === 0x52
        && bytes[1] === 0x49
        && bytes[2] === 0x46
        && bytes[3] === 0x46
        && bytes[8] === 0x57
        && bytes[9] === 0x45
        && bytes[10] === 0x42
        && bytes[11] === 0x50
    ) {
        return 'image/webp';
    }

    return '';
};

export const validateCatalogImageFile = async (file) => {
    const extension = file?.name?.split('.').pop()?.toLowerCase();
    const extensionMimeType = CATALOG_IMAGE_EXTENSIONS[extension];

    if (!file || !extensionMimeType || !CATALOG_IMAGE_TYPES.includes(file.type) || file.type !== extensionMimeType) {
        return INVALID_CATALOG_IMAGE_MESSAGE;
    }

    try {
        const signatureMimeType = getMimeTypeFromSignature(await readFileSignature(file));
        return signatureMimeType === extensionMimeType ? '' : INVALID_CATALOG_IMAGE_MESSAGE;
    } catch (error) {
        return INVALID_CATALOG_IMAGE_MESSAGE;
    }
};

const loadImage = (file) => new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(image);
    };

    image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error(INVALID_CATALOG_IMAGE_MESSAGE));
    };

    image.src = imageUrl;
});

const canvasToWebpBlob = (canvas, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (!blob) {
            reject(new Error('Não foi possível processar a imagem.'));
            return;
        }

        if (blob.type !== 'image/webp') {
            reject(new Error('O navegador não oferece suporte à conversão para WebP.'));
            return;
        }

        resolve(blob);
    }, 'image/webp', quality);
});

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        const base64 = result.includes(',') ? result.split(',')[1] : '';

        if (!base64) {
            reject(new Error('Não foi possível converter a imagem.'));
            return;
        }

        resolve(base64);
    };
    reader.onerror = () => reject(new Error('Não foi possível converter a imagem.'));
    reader.readAsDataURL(blob);
});

const createCanvasWithImage = (image, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Não foi possível preparar a imagem.');
    }

    context.drawImage(image, 0, 0, width, height);
    return canvas;
};

export const processCatalogImageFile = async (file, { maxHeight } = {}) => {
    const validationMessage = await validateCatalogImageFile(file);
    if (validationMessage) {
        throw new Error(validationMessage);
    }

    const image = await loadImage(file);

    if (file.size <= CATALOG_IMAGE_MAX_SIZE) {
        return {
            arquivo: await blobToBase64(file),
            nome: file.name,
            previewUrl: URL.createObjectURL(file),
            tamanho: file.size,
        };
    }

    const validMaxHeight = Number(maxHeight) > 0 ? Number(maxHeight) : image.height;
    const baseScale = image.height > validMaxHeight ? validMaxHeight / image.height : 1;
    const scaleSteps = CATALOG_IMAGE_SCALE_STEPS.map((step) => baseScale * step);
    let processedBlob = null;

    for (const scale of scaleSteps) {
        const targetWidth = Math.max(1, Math.round(image.width * scale));
        const targetHeight = Math.max(1, Math.round(image.height * scale));
        const canvas = createCanvasWithImage(image, targetWidth, targetHeight);

        for (const quality of CATALOG_IMAGE_QUALITY_STEPS) {
            processedBlob = await canvasToWebpBlob(canvas, quality);
            if (processedBlob.size <= CATALOG_IMAGE_MAX_SIZE) {
                break;
            }
        }

        if (processedBlob?.size <= CATALOG_IMAGE_MAX_SIZE) {
            break;
        }
    }

    if (!processedBlob || processedBlob.size > CATALOG_IMAGE_MAX_SIZE) {
        throw new Error('Não foi possível reduzir a imagem para no máximo 60 KB.');
    }

    return {
        arquivo: await blobToBase64(processedBlob),
        nome: file.name.replace(/\.[^.]+$/, '.webp'),
        previewUrl: URL.createObjectURL(processedBlob),
        tamanho: processedBlob.size,
    };
};

export const readImageFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const preview = typeof reader.result === 'string' ? reader.result : '';
        const base64 = preview.includes(',') ? preview.split(',')[1] : '';

        if (!base64) {
            reject(new Error('Não foi possível ler a imagem selecionada.'));
            return;
        }

        resolve({ base64, preview });
    };
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem selecionada.'));
    reader.readAsDataURL(file);
});
